import React, { useState } from 'react'
import { Shield, FileText, Check, X, AlertTriangle } from 'lucide-react'
import ReactMarkdown from 'react-markdown'

interface NdaClickwrapProps {
  tenantId: string
  userEmail: string
  userName?: string
  userCompany?: string
  documentId?: string
  ndaTitle: string
  ndaText: string
  ndaVersion: string
  onAccept: () => void
  onReject: () => void
  loading?: boolean
}

/**
 * Clickwrap NDA acceptance component
 * Displays NDA text with checkbox acceptance and audit trail
 */
const NdaClickwrap: React.FC<NdaClickwrapProps> = ({
  tenantId,
  userEmail,
  userName,
  userCompany,
  documentId,
  ndaTitle,
  ndaText,
  ndaVersion,
  onAccept,
  onReject,
  loading = false
}) => {
  const [agreed, setAgreed] = useState(false)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAccept = async () => {
    if (!agreed) {
      setError('You must read and agree to the NDA to continue')
      return
    }

    setAccepting(true)
    setError(null)

    try {
      const response = await fetch('/api/nda/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          userEmail,
          userName,
          userCompany,
          documentId,
          accepted: true
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to accept NDA')
      }

      const data = await response.json()
      console.log('NDA accepted:', data)

      // Call parent callback
      onAccept()

    } catch (err: any) {
      console.error('NDA acceptance error:', err)
      setError(err.message || 'Failed to accept NDA')
      setAccepting(false)
    }
  }

  const handleReject = async () => {
    try {
      // SECURITY: Ensure NDA rejection is logged before proceeding
      // This is critical for compliance and legal audit trail
      const response = await fetch('/api/nda/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          userEmail,
          userName,
          userCompany,
          documentId,
          accepted: false
        })
      })

      if (!response.ok) {
        console.error('Failed to log NDA rejection:', response.status)
        // Still proceed with rejection - user shouldn't be blocked,
        // but log the audit trail failure for investigation
        setError('Unable to record your decision. Your rejection has been noted locally.')
      }

      onReject()
    } catch (err) {
      console.error('NDA rejection logging error:', err)
      // Proceed with rejection even if logging fails (network error)
      // but warn the user
      setError('Network error while recording your decision. Your rejection has been noted locally.')
      onReject()
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-2xl max-w-full sm:max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{ndaTitle}</h2>
              <p className="text-sm text-blue-100">Version {ndaVersion}</p>
            </div>
          </div>
          <button
            onClick={handleReject}
            disabled={accepting}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* NDA Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-900">NDA Agreement Required</p>
              <p className="text-sm text-blue-700 mt-1">
                You must read and accept this Non-Disclosure Agreement before accessing confidential documents.
              </p>
            </div>
          </div>

          <div className="prose prose-sm max-w-none">
            <ReactMarkdown>{ndaText}</ReactMarkdown>
          </div>

          {/* User Info Display */}
          <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Agreement Details</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Email:</span> {userEmail}</p>
              {userName && <p><span className="font-medium">Name:</span> {userName}</p>}
              {userCompany && <p><span className="font-medium">Company:</span> {userCompany}</p>}
              <p><span className="font-medium">Date:</span> {new Date().toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-2">
                Your acceptance will be recorded with your IP address and timestamp for audit purposes.
              </p>
            </div>
          </div>
        </div>

        {/* Footer with Agreement Checkbox */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <label className="flex items-start gap-3 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => {
                setAgreed(e.target.checked)
                setError(null)
              }}
              disabled={accepting}
              className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700">
              I have read and agree to the terms of this Non-Disclosure Agreement. I understand that this agreement is legally binding and that unauthorized disclosure of confidential information may result in legal action.
            </span>
          </label>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={handleReject}
              disabled={accepting}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={!agreed || accepting}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md hover:from-blue-700 hover:to-indigo-700 font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {accepting ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Accepting...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  I Accept
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NdaClickwrap
